import { forwardRef, ElementRef, ForwardedRef, useState } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";

import {
  SingleDatePicker as ReactDatesSingleDatePicker,
  SingleDatePickerShape,
} from "react-dates";
import moment from "moment";
import {
  BaseDatePicker,
  Caption,
  createIcon,
  StyledDatePickerContainer,
  NavigationButton,
} from "../common";
import { MonthYearSelector } from "./MonthYearSelector";
import { Icon } from "../Icon";
import { FlattenSimpleInterpolation } from "styled-components";

type DatePickerProps = Omit<
  SingleDatePickerShape,
  "date" | "onDateChange" | "isOutsideRange"
> & {
  date?: Date | null;
  onDateChange?: (date?: Date) => void;
  isOutsideRange?: ((day: Date) => boolean) | undefined;
  caption?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  showMonthYearSelector?: boolean;
  styles?: FlattenSimpleInterpolation;
};

export const SingleDatePicker = forwardRef(
  (
    props: DatePickerProps,
    ref: ForwardedRef<ElementRef<typeof ReactDatesSingleDatePicker>>,
  ) => {
    const [focused, setFocused] = useState(props.focused);
    const [date, setDate] = useState<moment.Moment | null>(
      props.date ? moment(props.date) : null,
    );

    return (
      <BaseDatePicker
        disabled={props.disabled}
        errorMessage={props.errorMessage}
        isRequired={props.isRequired}
        label={props.label}
        id={props.id}
      >
        <StyledDatePickerContainer
          disabled={props.disabled}
          readOnly={props.readOnly}
          hasError={!!props.errorMessage}
          hasSelectors={!!props.showMonthYearSelector}
          styles={props.styles}
        >
          <ReactDatesSingleDatePicker
            {...props}
            ref={ref}
            disabled={props.disabled || props.readOnly}
            required={props.isRequired}
            numberOfMonths={props.numberOfMonths ?? 1}
            daySize={props.daySize ?? 32}
            navPrev={
              <NavigationButton className="nav-prev">
                <Icon iconSize="small" iconName="chevron-left" plain />
              </NavigationButton>
            }
            navNext={
              <NavigationButton className="nav-next">
                <Icon iconSize="small" iconName="chevron-right" plain />
              </NavigationButton>
            }
            hideKeyboardShortcutsPanel={
              props.hideKeyboardShortcutsPanel ?? true
            }
            customInputIcon={
              props.customInputIcon ??
              createIcon(
                props.disabled ?? false,
                props.readOnly ?? false,
                focused ?? false,
              )
            }
            inputIconPosition={props.inputIconPosition ?? "after"}
            block
            noBorder
            small
            date={date}
            focused={focused}
            onDateChange={(newDate: moment.Moment | null) => {
              setDate(newDate);
              props.onDateChange && props.onDateChange(newDate?.toDate());
            }}
            isOutsideRange={(day: moment.Moment) => {
              return props.isOutsideRange
                ? props.isOutsideRange(day.toDate())
                : false;
            }}
            onFocusChange={(focused) => {
              setFocused(focused.focused);
              props.onFocusChange && props.onFocusChange(focused);
            }}
            {...(props.showMonthYearSelector && {
              renderMonthElement: MonthYearSelector,
            })}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- there's a bug in @types/react-dates
            renderMonthText={props.renderMonthText as any}
          />
        </StyledDatePickerContainer>
        {(props.caption || props.errorMessage) && (
          <Caption
            isDisabled={props.disabled || props.readOnly}
            hasError={!!props.errorMessage}
          >
            {props.errorMessage || props.caption}
          </Caption>
        )}
      </BaseDatePicker>
    );
  },
);

SingleDatePicker.displayName = "SingleDatePicker";
